import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Nav, Badge, Button, Dropdown, Modal, Alert, OverlayTrigger, Tooltip, Collapse } from 'react-bootstrap';
import { Plus, Search, Trash2, ExternalLink, MoreVertical, HelpCircle, ChevronDown, ChevronUp, Ban } from 'lucide-react';
import type { ProductData, CompetitorProduct } from '../models/types';
import { hasVariantMismatch } from '../models/types';
import InsertCompetitorProductModal from './InsertCompetitorProductModal';
import type { CompetitorClient, MatchedCompetitorProductRequest, MatchedCompetitorProductsRequest } from '../../../../infrastructure/CompetitorClient';
import { useInsertCompetitorProduct } from '../../../../apps/hooks/competitor/useInsertCompetitorProduct';
import type { Competitor } from '../../../../models/ProductTypes';

interface CompetitorSelectionSectionProps {
  readonly selectedProduct: ProductData;
  readonly activeCompetitor: string | null;
  readonly selectedProducts: Record<string, Record<string, string>>;
  readonly handleCompetitorProductSelect: (product?: CompetitorProduct) => void;
  readonly isProductSelected: (skinSeoulSku: string, competitor: string, competitorSku: string) => boolean;
  readonly setActiveCompetitor: (competitor: string | null) => void;
  readonly competitorClient: CompetitorClient;
  readonly onProductDataUpdate: (updatedProduct: ProductData) => void;
  readonly onRefreshProductData: (sku: string) => Promise<void>;
  readonly actionedBy?: string | undefined;
}

const competitorSearchUrls: Record<string, string> = {
  YesStyle: 'https://www.yesstyle.com/en/list.html?q=',
  Stylevana: 'https://www.stylevana.com/en_US/catalogsearch/result/?q=',
  StyleKorean: 'https://www.stylekorean.com/shop/search_result.php?keyword=',
  OliveYoung: 'https://global.oliveyoung.com/display/search?query=',
  Amazon: 'https://www.amazon.com/s?k=',
};

function normalizationRatioOf(competitorProduct: CompetitorProduct): number | undefined {
  return competitorProduct.normalizationRatio ?? competitorProduct.normalization?.computedRatio;
}

function normalizedPriceOf(competitorProduct: CompetitorProduct): number | undefined {
  return competitorProduct.normalizedPrice ?? competitorProduct.normalization?.normalizedPrice;
}

function buildIdentifier(competitorProduct: CompetitorProduct): MatchedCompetitorProductRequest {
  return {
    sku: competitorProduct.sku,
    possibility: competitorProduct.possibility ?? 1,
  };
}

export default function CompetitorSelectionSection({
  selectedProduct,
  activeCompetitor,
  selectedProducts,
  handleCompetitorProductSelect,
  isProductSelected,
  setActiveCompetitor,
  competitorClient,
  onProductDataUpdate,
  onRefreshProductData,
  actionedBy,
}: CompetitorSelectionSectionProps) {
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [showUnmatchModal, setShowUnmatchModal] = useState(false);
  const [selectedCompetitorProduct, setSelectedCompetitorProduct] = useState<CompetitorProduct | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [expandedNormalization, setExpandedNormalization] = useState<string | null>(null);

  const { handleInsert } = useInsertCompetitorProduct(
    competitorClient,
    selectedProduct,
    onProductDataUpdate,
    setActiveCompetitor,
  );

  const skinSeoulProduct = selectedProduct.skinSeoulProduct;

  const handleCompetitorSearch = (competitor: string) => {
    if (!skinSeoulProduct?.name) return;
    const searchTerm = encodeURIComponent(skinSeoulProduct.name);
    window.open(`${competitorSearchUrls[competitor]}${searchTerm}`, '_blank');
  };

  const getCompetitorProducts = (): CompetitorProduct[] => {
    if (!activeCompetitor) return [];
    const machineProducts =
      selectedProduct.machineMatchedProducts.find((c) => c.competitor === activeCompetitor)?.competitorProducts ?? [];
    const humanProducts =
      selectedProduct.humanMatchedProducts?.find((c) => c.competitor === activeCompetitor)?.competitorProducts ?? [];

    const humanSkus = new Set(humanProducts.map((p) => p.sku));
    const mergedProducts: CompetitorProduct[] = [
      ...humanProducts.map((p) => ({ ...p, matchType: 'human' as const })),
      ...machineProducts.filter((p) => !humanSkus.has(p.sku)).map((p) => ({ ...p, matchType: 'machine' as const })),
    ];
    return mergedProducts;
  };

  const getAllCompetitors = () => {
    const competitorSet = new Map<string, number>();
    selectedProduct.machineMatchedProducts.forEach((c) => {
      competitorSet.set(c.competitor, (competitorSet.get(c.competitor) || 0) + c.competitorProducts.length);
    });
    selectedProduct.humanMatchedProducts?.forEach((c) => {
      competitorSet.set(c.competitor, (competitorSet.get(c.competitor) || 0) + c.competitorProducts.length);
    });
    return Array.from(competitorSet.entries())
      .filter(([, count]) => count > 0)
      .map(([competitor]) => ({ competitor }));
  };

  const competitorsWithProducts = getAllCompetitors();
  const competitorProducts = getCompetitorProducts();
  const visibleCompetitorSkus = competitorProducts.map((competitorProduct) => competitorProduct.sku);

  const sharedSkuCountsQuery = useQuery({
    queryKey: ['competitor-shared-sku-counts', activeCompetitor, visibleCompetitorSkus],
    queryFn: async () => {
      const counts = await Promise.all(
        visibleCompetitorSkus.map(async (competitorSku) => {
          const total = await competitorClient.countSkinSeoulProductsSharingCompetitorProduct(competitorSku);
          return [competitorSku, Math.max(0, total - 1)] as const;
        }),
      );
      return new Map(counts);
    },
    enabled: visibleCompetitorSkus.length > 0,
    staleTime: 60000,
  });

  const sharedSkuCount = (competitorSku: string): number | undefined => sharedSkuCountsQuery.data?.get(competitorSku);

  const getFirstImageUrl = (urls: any): string => {
    if (!urls) return '';
    if (Array.isArray(urls)) return urls[0]?.trim() || '';
    if (typeof urls === 'string') return urls.split(',')[0]?.trim() || '';
    return '';
  };

  const handleUnmatch = async () => {
    if (!selectedCompetitorProduct || !activeCompetitor) return;
    setIsActionLoading(true);
    setActionError(null);
    try {
      await competitorClient.unmatchCompetitor({
        skinSeoulSku: skinSeoulProduct.sku,
        competitor: activeCompetitor as Competitor,
        competitorSku: selectedCompetitorProduct.sku,
        actionedBy,
      });
      setShowUnmatchModal(false);
      setSelectedCompetitorProduct(null);
      await onRefreshProductData(skinSeoulProduct.sku);
      toast.success('Competitor product unmatched.');
    } catch (err) {
      setActionError('Failed to unmatch competitor product.');
      toast.error('Failed to unmatch competitor product.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const openUnmatchModal = (product: CompetitorProduct) => {
    setSelectedCompetitorProduct(product);
    setActionError(null);
    setShowUnmatchModal(true);
  };

  const handleVerifyNoCompetitor = async (verified: boolean) => {
    setIsActionLoading(true);
    setActionError(null);
    try {
      await competitorClient.verifyNoCompetitor({ skinSeoulSku: skinSeoulProduct.sku, verified, actionedBy });
      await onRefreshProductData(skinSeoulProduct.sku);
      toast.success(verified ? 'Marked as no valid competitor. This product is now treated as exclusive.' : 'Removed the no-competitor decision.');
    } catch {
      setActionError('Failed to save the review decision.');
      toast.error('Failed to save the review decision.');
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleMatchAndSave = async (product: CompetitorProduct) => {
    if (!activeCompetitor) return;

    // Skip if already human-matched
    const existingHuman = selectedProduct.humanMatchedProducts || [];
    const alreadyMatched = existingHuman.some(
      (hm) => hm.competitor === activeCompetitor && hm.competitorProducts.some((cp: any) => cp.sku === product.sku),
    );
    if (alreadyMatched) {
      handleCompetitorProductSelect(product);
      return;
    }

    setIsActionLoading(true);
    setActionError(null);
    try {
      const updatedHuman: MatchedCompetitorProductsRequest[] = [];
      let competitorFound = false;

      for (const hm of existingHuman) {
        if (hm.competitor === activeCompetitor) {
          competitorFound = true;
          const identifiers = hm.competitorProducts.map((cp: any) => buildIdentifier(cp));
          identifiers.push(buildIdentifier(product));
          updatedHuman.push({
            competitor: hm.competitor as Competitor,
            competitorProductIdentifiers: identifiers,
          });
        } else {
          updatedHuman.push({
            competitor: hm.competitor as Competitor,
            competitorProductIdentifiers: hm.competitorProducts.map((cp: any) => buildIdentifier(cp)),
          });
        }
      }

      if (!competitorFound) {
        updatedHuman.push({
          competitor: activeCompetitor as Competitor,
          competitorProductIdentifiers: [buildIdentifier(product)],
        });
      }

      const existingMachine: MatchedCompetitorProductsRequest[] = selectedProduct.machineMatchedProducts.map((mm) => ({
        competitor: mm.competitor as Competitor,
        competitorProductIdentifiers: mm.competitorProducts.map((cp: any) => buildIdentifier(cp)),
      }));

      await competitorClient.matchCompetitorProduct({
        skinSeoulSku: skinSeoulProduct.sku,
        humanMatchedProducts: updatedHuman,
        machineMatchedProducts: existingMachine,
        actionedBy,
      });

      // Update local product data
      const updatedProduct = { ...selectedProduct };
      const humanMatched = [...(updatedProduct.humanMatchedProducts || [])];
      const compIdx = humanMatched.findIndex((hm) => hm.competitor === activeCompetitor);

      if (compIdx >= 0) {
        const existing = humanMatched[compIdx]!;
        humanMatched[compIdx] = {
          ...existing,
          competitorProducts: [...existing.competitorProducts, { ...product, matchType: 'human' as const }],
        };
      } else {
        humanMatched.push({
          competitor: activeCompetitor,
          competitorProducts: [{ ...product, matchType: 'human' as const }],
        });
      }

      updatedProduct.humanMatchedProducts = humanMatched;
      onProductDataUpdate(updatedProduct);
      handleCompetitorProductSelect(product);
      await onRefreshProductData(skinSeoulProduct.sku);
      toast.success(`Matched with ${product.normalizedName || product.sku}.`);
    } catch (err) {
      setActionError('Failed to save match.');
      toast.error('Failed to save match.');
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3 p-3 d-flex flex-column h-100" style={{ overflow: 'hidden' }}>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="fw-bold" style={{ fontSize: '1rem' }}>
          Competitor Products
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <Search size={14} className="me-1" />
              Search
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {Object.keys(competitorSearchUrls).map((c) => (
                <Dropdown.Item key={c} onClick={() => handleCompetitorSearch(c)}>
                  {c}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Button variant="outline-primary" size="sm" onClick={() => setShowInsertModal(true)}>
            <Plus size={14} className="me-1" />
            Insert
          </Button>
          <Button
            variant={selectedProduct.humanVerifiedNoCompetitor ? 'success' : 'outline-warning'}
            size="sm"
            onClick={() => handleVerifyNoCompetitor(!selectedProduct.humanVerifiedNoCompetitor)}
            disabled={isActionLoading}
            title="Record that this product has no valid competitor, so it is priced as SkinSeoul exclusive"
          >
            <Ban size={14} className="me-1" />
            {selectedProduct.humanVerifiedNoCompetitor ? 'Marked exclusive' : 'No valid competitor'}
          </Button>
        </div>
      </div>

      {competitorsWithProducts.length > 0 ? (
        <>
          <Nav
            variant="tabs"
            activeKey={activeCompetitor ?? ''}
            onSelect={(key) => setActiveCompetitor(key)}
            className="mb-3 border-bottom-0"
          >
            {competitorsWithProducts.map((c) => {
              const isMatched = selectedProducts?.[skinSeoulProduct.sku]?.[c.competitor] !== undefined;
              return (
                <Nav.Item key={c.competitor}>
                  <Nav.Link eventKey={c.competitor} className={isMatched ? 'text-success' : ''}>
                    {c.competitor}
                    {isMatched && (
                      <Badge bg="success" className="ms-1">
                        ✓
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>

          <div style={{ overflowY: 'auto', flexGrow: 1 }}>
            {activeCompetitor && competitorProducts.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {competitorProducts.map((product, index) => {
                  const isSelected = isProductSelected(skinSeoulProduct.sku, activeCompetitor, product.sku);
                  const matchType = product.matchType || 'machine';

                  return (
                    <div
                      key={product.sku}
                      className={`p-3 border rounded ${isSelected ? 'border-success border-2 bg-success bg-opacity-10' : 'bg-white'}`}
                      style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                      onClick={() => handleMatchAndSave(product)}
                    >
                      <div className="d-flex gap-3">
                        <div className="position-relative" style={{ minWidth: '80px' }}>
                          {product.productImageUrls ? (
                            <img
                              src={getFirstImageUrl(product.productImageUrls)}
                              alt={product.normalizedName}
                              style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '6px' }}
                            />
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center bg-light text-muted"
                              style={{ width: '80px', height: '80px', borderRadius: '6px', fontSize: '0.7rem' }}
                            >
                              No image
                            </div>
                          )}
                          <Badge
                            bg="dark"
                            className="position-absolute"
                            style={{
                              top: '-6px',
                              left: '-6px',
                              borderRadius: '50%',
                              width: '22px',
                              height: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {index + 1}
                          </Badge>
                          {isSelected && (
                            <Badge
                              bg="success"
                              className="position-absolute"
                              style={{
                                bottom: '-6px',
                                right: '-6px',
                                borderRadius: '50%',
                                width: '22px',
                                height: '22px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              ✓
                            </Badge>
                          )}
                          {matchType === 'human' && (
                            <Badge
                              bg="success"
                              className="position-absolute"
                              style={{
                                top: '-6px',
                                right: '-6px',
                                borderRadius: '4px',
                                width: '22px',
                                height: '22px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                              }}
                              title="Human Verified"
                            >
                              ✓
                            </Badge>
                          )}
                        </div>

                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="mb-0" style={{ fontSize: '0.9rem' }}>
                              {product.normalizedName}
                            </h6>
                            <div className="d-flex gap-1 align-items-center">
                              {hasVariantMismatch(skinSeoulProduct.name, product.normalizedName || product.name) && (
                                <Badge
                                  bg="warning"
                                  text="dark"
                                  style={{ fontSize: '0.65rem' }}
                                  title="The names differ by several distinguishing words. Check this is the same variant."
                                >
                                  Check variant
                                </Badge>
                              )}
                              {matchType === 'human' ? (
                                <Badge bg="info" style={{ fontSize: '0.65rem' }} title="Chosen by a reviewer, not a match score">
                                  Verified
                                </Badge>
                              ) : (
                                product.possibility != null && (
                                  <Badge
                                    bg={product.possibility > 0.8 ? 'success' : product.possibility > 0.6 ? 'warning' : 'danger'}
                                    style={{ fontSize: '0.65rem' }}
                                  >
                                    {Math.round(product.possibility * 100)}%
                                  </Badge>
                                )
                              )}
                            </div>
                          </div>

                          <div className="small text-muted mb-1">
                            SKU: {product.sku} | Brand: {product.brand}
                          </div>
                          {(sharedSkuCount(product.sku) ?? 0) > 0 && (
                            <div className="small text-muted fw-bold mb-1">
                              Also matched to {sharedSkuCount(product.sku)} other SkinSeoul{' '}
                              {sharedSkuCount(product.sku) === 1 ? 'SKU' : 'SKUs'}
                            </div>
                          )}
                          <div className="small text-muted mb-2">
                            Size: {product.size || 'N/A'} | Pack: {product.pack || 'N/A'}
                          </div>

                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              {(() => {
                                // Find USD price (backend calculates normalizedPrice in USD)
                                const usdPrice = product.price?.find(p => p.currency === 'USD');
                                const fallbackPrice = product.price?.[0];

                                const salesPrice =
                                  usdPrice?.salesPrice ?? fallbackPrice?.salesPrice ?? product.salePrice;
                                const originalPrice =
                                  usdPrice?.originalPrice ?? fallbackPrice?.originalPrice ?? product.originalPrice;
                                const currency = usdPrice?.currency ?? fallbackPrice?.currency ?? 'USD';

                                const ratio =
                                  product.normalizationRatio ?? product.normalization?.computedRatio;
                                const normalizedPrice =
                                  product.normalizedPrice ?? product.normalization?.normalizedPrice;

                                return (
                                  <>
                                    {salesPrice != null && (
                                      <span className="fw-bold text-primary">
                                        {currency === 'USD' ? '$' : `${currency} `}{salesPrice.toFixed(2)}
                                      </span>
                                    )}
                                    {salesPrice != null && originalPrice != null && originalPrice !== salesPrice && (
                                      <span className="text-decoration-line-through text-muted small">
                                        {currency === 'USD' ? '$' : `${currency} `}{originalPrice.toFixed(2)}
                                      </span>
                                    )}
                                    {salesPrice == null && originalPrice != null && (
                                      <span className="fw-bold text-primary">
                                        {currency === 'USD' ? '$' : `${currency} `}{originalPrice.toFixed(2)}
                                      </span>
                                    )}
                                    {normalizedPrice != null && ratio != null && (
                                      <span className="small text-muted">
                                        (×{ratio.toFixed(2)} →{' '}
                                        <span className="text-success fw-bold">${normalizedPrice.toFixed(2)}</span>
                                        {currency !== 'USD' && <span className="text-warning ms-1">⚠ USD</span>}
                                        )
                                      </span>
                                    )}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 ms-1"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedNormalization(
                                          expandedNormalization === product.sku ? null : product.sku
                                        );
                                      }}
                                      style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                                      title="Normalization settings"
                                    >
                                      {expandedNormalization === product.sku ? (
                                        <ChevronUp size={14} />
                                      ) : (
                                        <ChevronDown size={14} />
                                      )}
                                    </Button>
                                    {(salesPrice != null || originalPrice != null) && (
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={
                                          <Tooltip>
                                            <div className="text-start">
                                              <strong>Price Information ({currency})</strong>
                                              {salesPrice != null && (
                                                <div className="mt-1">
                                                  • <strong>Sale Price:</strong> {currency === 'USD' ? '$' : `${currency} `}{salesPrice.toFixed(2)} (current selling price)
                                                </div>
                                              )}
                                              {originalPrice != null && (
                                                <div>
                                                  • <strong>Original Price:</strong> {currency === 'USD' ? '$' : `${currency} `}{originalPrice.toFixed(2)} (regular price)
                                                </div>
                                              )}
                                              {salesPrice != null && originalPrice != null && originalPrice !== salesPrice && (
                                                <div className="text-success">
                                                  <strong>Discount:</strong> {currency === 'USD' ? '$' : `${currency} `}{(originalPrice - salesPrice).toFixed(2)} ({(((originalPrice - salesPrice) / originalPrice) * 100).toFixed(0)}% off)
                                                </div>
                                              )}
                                              {normalizedPrice != null && ratio != null && (
                                                <>
                                                  <div className="mt-2">
                                                    <strong>Price Normalization (USD):</strong>
                                                  </div>
                                                  <div>
                                                    • <strong>Ratio:</strong> ×{ratio.toFixed(2)} (size/pack adjustment)
                                                  </div>
                                                  <div>
                                                    • <strong>Normalized Price:</strong> ${normalizedPrice.toFixed(2)} (adjusted to match our product size)
                                                  </div>
                                                  {currency !== 'USD' && (
                                                    <div className="text-warning mt-1">
                                                      ⚠ Normalized price is calculated in USD regardless of display currency
                                                    </div>
                                                  )}
                                                </>
                                              )}
                                            </div>
                                          </Tooltip>
                                        }
                                      >
                                        <HelpCircle
                                          size={14}
                                          className="text-muted"
                                          style={{ cursor: 'pointer', marginLeft: '4px' }}
                                        />
                                      </OverlayTrigger>
                                    )}
                                  </>
                                );
                              })()}
                            </div>

                            <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                href={product.productLink}
                                target="_blank"
                                as="a"
                                title={`View on ${activeCompetitor}`}
                              >
                                <ExternalLink size={12} />
                              </Button>
                              <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                  <MoreVertical size={12} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item className="text-danger" onClick={() => openUnmatchModal(product)}>
                                    <Trash2 size={14} className="me-2" />
                                    Unmatch
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                        </div>
                      </div>

                      <Collapse in={expandedNormalization === product.sku}>
                        <div className="mt-2 pt-2 border-top">
                          <h6 className="small fw-bold mb-2">Normalization Details</h6>
                          <div className="small">
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted">Competitor Size/Pack:</span>
                              <span className="fw-bold">
                                {product.size || 'N/A'} × {product.pack || '1'} pack
                              </span>
                            </div>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="text-muted">Our Product Size/Pack:</span>
                              <span className="fw-bold">
                                {skinSeoulProduct.size || 'N/A'} × {skinSeoulProduct.pack || '1'} pack
                              </span>
                            </div>
                            <hr className="my-2" />
                            {product.normalization?.comparable === false ? (
                              <div className="d-flex justify-content-between">
                                <span className="text-muted">Comparison:</span>
                                <span className="fw-bold text-warning">Not comparable</span>
                              </div>
                            ) : (
                              <>
                                <div className="d-flex justify-content-between mb-1">
                                  <span className="text-muted">Computed Ratio:</span>
                                  <span className="fw-bold">
                                    {normalizationRatioOf(product) === undefined ? '—' : `×${normalizationRatioOf(product)!.toFixed(2)}`}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Normalized Price:</span>
                                  <span className="fw-bold text-success">
                                    {normalizedPriceOf(product) === undefined ? 'No USD price' : `$${normalizedPriceOf(product)!.toFixed(2)} USD`}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                {activeCompetitor ? 'No products for this competitor' : 'Select a competitor tab'}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center text-muted py-4">
          <div className="mb-2">No competitor products found</div>
          {selectedProduct.humanVerifiedNoCompetitor ? (
            <div className="small">Reviewed: no valid competitor. This product is priced as SkinSeoul exclusive.</div>
          ) : (
            <div className="small">Mark it as having no valid competitor to clear it from the review queue.</div>
          )}
        </div>
      )}

      <InsertCompetitorProductModal show={showInsertModal} onHide={() => setShowInsertModal(false)} onSubmit={handleInsert} />

      {selectedCompetitorProduct && (
        <>
          <Modal show={showUnmatchModal} onHide={() => setShowUnmatchModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Unmatch</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {actionError && <Alert variant="danger">{actionError}</Alert>}
              <p>
                Are you sure you want to unmatch <strong>{selectedCompetitorProduct.normalizedName}</strong> from{' '}
                <strong>{skinSeoulProduct.name}</strong>?
              </p>
              <p className="text-muted small">This competitor product will no longer affect pricing calculations.</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowUnmatchModal(false)} disabled={isActionLoading}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleUnmatch} disabled={isActionLoading}>
                {isActionLoading ? 'Processing...' : 'Unmatch'}
              </Button>
            </Modal.Footer>
          </Modal>

        </>
      )}
    </div>
  );
}
